import React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

const forgotSchema = z.object({
  email: z.string().email("Invalid email address.")
});

export type ForgotFormData = z.infer<typeof forgotSchema>;

interface ForgotPasswordFormProps {
  onSubmit: (data: ForgotFormData) => Promise<void>;
  authError?: string;
  successMessage?: string;
}

const UnderlineField: React.FC<{
  id: string;
  label: string;
  type?: string;
  autoComplete?: string;
  registerProps: any;
  error?: string;
  errorId?: string;
  disabled?: boolean;
}> = ({
  id,
  label,
  type = "text",
  autoComplete,
  registerProps,
  error,
  errorId,
  disabled
}) => (
  <div>
    <label htmlFor={id} className="font-body text-intro-text text-slate-900">
      {label}
    </label>

    <div className="mt-3 border-b border-black">
      <input
        id={id}
        type={type}
        autoComplete={autoComplete}
        disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        aria-invalid={!!error || undefined}
        aria-describedby={error ? errorId : undefined}
        className={[
          "w-full bg-transparent py-3",
          "font-body text-p-body-text text-slate-900",
          "outline-none",
          "placeholder:text-slate-400",
          "disabled:opacity-100"
        ].join(" ")}
        {...registerProps}
      />
    </div>

    {error && (
      <p id={errorId} className="mt-2 typo-p-supporting-text">
        {error}
      </p>
    )}
  </div>
);

const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({
  onSubmit,
  authError,
  successMessage
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm<ForgotFormData>({
    resolver: zodResolver(forgotSchema)
  });

  const buttonBase = [
    "inline-flex items-center justify-between",
    "w-full",
    "rounded-full bg-[#f3dfae] px-6 py-3",
    "text-[#151A3A] hover:opacity-95 transition-opacity",
    "disabled:opacity-50"
  ].join(" ");

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-col gap-6">
      {authError && (
        <div className="rounded-2xl border border-red-300 bg-red-50 p-3">
          <p className="typo-p-supporting-text text-black">{authError}</p>
        </div>
      )}

      {successMessage && (
        <div className="rounded-2xl border border-green-300 bg-green-50 p-3">
          <p className="typo-p-supporting-text text-black">{successMessage}</p>
        </div>
      )}

      <UnderlineField
        id="email"
        label="Enter your registered email"
        type="email"
        autoComplete="email"
        registerProps={register("email")}
        error={errors.email?.message}
        errorId="forgot-email-error"
        disabled={isSubmitting}
      />

      <button type="submit" disabled={isSubmitting} className={buttonBase}>
        <span className="typo-button-text whitespace-nowrap text-left">
          {isSubmitting ? "SENDING…" : "SEND RESET LINK"}
        </span>
        <svg
          aria-hidden="true"
          viewBox="0 0 24 24"
          className="h-5 w-5"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M5 12h12" />
          <path d="M13 6l6 6-6 6" />
        </svg>
      </button>
    </form>
  );
};

export default ForgotPasswordForm;
